/**
 * The flat-DOM entry gate.
 *
 * This must be plain DOM: it renders before any XR session exists, so no
 * in-world panel could take its place. Its only job is to establish that the
 * device can do passthrough AR and to give the user a gesture to start from —
 * `requestSession` requires one.
 *
 * Deliberately NOT gated on anything else. An earlier version disabled the
 * button until a WebSocket connected, which meant that when the socket went
 * away the user could never enter the experience at all.
 */

export type WelcomeState = 'checking' | 'ready' | 'unsupported' | 'starting' | 'failed';

export interface WelcomeCard {
  /** Reflect a new state in the card. */
  set(state: WelcomeState, detail?: string): void;
  /** Show or hide the whole card (hidden while a session runs). */
  show(visible: boolean): void;
  /** Called when the user commits. Return a promise; errors surface on the card. */
  onEnter(handler: () => Promise<void>): void;
}

const LABELS: Record<WelcomeState, string> = {
  checking: 'Checking headset…',
  ready: 'Enter',
  unsupported: 'Headset required',
  starting: 'Starting…',
  failed: 'Try again',
};

const NOTES: Partial<Record<WelcomeState, string>> = {
  unsupported:
    'This experience needs a Meta Quest headset. Open this page in the Quest browser.',
};

export function mountWelcomeCard(root: Document = document): WelcomeCard {
  const card = root.getElementById('intro');
  const button = root.getElementById('enter-xr') as HTMLButtonElement | null;
  const note = root.getElementById('intro-note');

  if (!card || !button || !note) {
    throw new Error('Welcome card markup missing (#intro / #enter-xr / #intro-note)');
  }

  let handler: (() => Promise<void>) | undefined;
  let state: WelcomeState = 'checking';

  const render = (detail?: string): void => {
    button.textContent = LABELS[state];
    button.disabled = state !== 'ready' && state !== 'failed';
    const message = detail ?? NOTES[state] ?? '';
    note.textContent = message;
    note.classList.toggle('error', state === 'failed' || state === 'unsupported');
  };

  button.addEventListener('click', () => {
    if (!handler || button.disabled) return;
    state = 'starting';
    render();
    handler()
      .then(() => {
        // The session-start listener hides the card; nothing to do here.
      })
      .catch((error: unknown) => {
        state = 'failed';
        render(describeSessionFailure(error));
      });
  });

  render();

  return {
    set(next, detail) {
      state = next;
      render(detail);
    },
    show(visible) {
      card.classList.toggle('hidden', !visible);
      if (visible && state === 'starting') {
        // Session ended before it really began — offer the button back.
        state = 'ready';
        render();
      }
    },
    onEnter(next) {
      handler = next;
    },
  };
}

/**
 * Turn a `requestSession` rejection into something a user can act on. The raw
 * DOMException messages are accurate but unhelpful in a headset.
 */
export function describeSessionFailure(error: unknown): string {
  const name = error instanceof DOMException ? error.name : '';
  const message = error instanceof Error ? error.message : String(error);

  if (name === 'NotSupportedError') {
    return 'This headset reported that passthrough AR is unavailable.';
  }
  if (name === 'SecurityError') {
    return 'The browser blocked the session. Make sure the page is served over HTTPS.';
  }
  if (name === 'InvalidStateError') {
    return 'A session is already running. Close it and try again.';
  }
  return message || 'Could not start the session.';
}

/**
 * Probe passthrough support. Resolves false rather than throwing when WebXR is
 * absent entirely, which is the common case in a desktop browser.
 */
export async function isPassthroughSupported(): Promise<boolean> {
  const xr = navigator.xr;
  if (!xr?.isSessionSupported) return false;
  try {
    return await xr.isSessionSupported('immersive-ar');
  } catch {
    return false;
  }
}
